import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';

import {
  GEREEDSCHAPPEN,
  isLeeg,
  metPin,
  ongedaan,
  tekenAlles,
  type Gereedschap,
  type Punt,
  type Vorm,
} from '@/lib/melden/tekenen';

interface Props {
  basis: HTMLCanvasElement;
  vormen: Vorm[];
  onVormen: (v: Vorm[]) => void;
  /** Pin position as a fraction of the screenshot; null when the pin is undone. */
  onPin: (p: { x: number; y: number } | null) => void;
}

const KNOP =
  'h-8 rounded-sm border border-border bg-bg-secondary px-2.5 text-[12.5px] text-text-secondary ' +
  'hover:text-text-primary aria-pressed:border-accent aria-pressed:text-accent disabled:opacity-50';

/** P4: box, arrow, pen, black-out and one pin on the screenshot, with undo. */
export function TekenCanvas({ basis, vormen, onVormen, onPin }: Props) {
  const { t } = useTranslation();
  const ref = useRef<HTMLCanvasElement>(null);
  const [gereedschap, setGereedschap] = useState<Gereedschap>('kader');
  const [schets, setSchets] = useState<Vorm | null>(null);

  useEffect(() => {
    const ctx = ref.current?.getContext('2d');
    if (ctx) tekenAlles(ctx, basis, schets ? [...vormen, schets] : vormen);
  }, [basis, vormen, schets]);

  function punt(e: ReactPointerEvent<HTMLCanvasElement>): Punt {
    const r = e.currentTarget.getBoundingClientRect();
    return {
      x: ((e.clientX - r.left) / Math.max(r.width, 1)) * basis.width,
      y: ((e.clientY - r.top) / Math.max(r.height, 1)) * basis.height,
    };
  }

  function neer(e: ReactPointerEvent<HTMLCanvasElement>) {
    if (e.button !== 0) return;
    const p = punt(e);
    if (gereedschap === 'pin') {
      onVormen(metPin(vormen, p));
      onPin({ x: p.x / basis.width, y: p.y / basis.height });
      return;
    }
    e.currentTarget.setPointerCapture(e.pointerId);
    setSchets(gereedschap === 'pen' ? { soort: 'pen', punten: [p] } : { soort: gereedschap, van: p, tot: p });
  }

  function beweeg(e: ReactPointerEvent<HTMLCanvasElement>) {
    if (!schets) return;
    const p = punt(e);
    if (schets.soort === 'pen') setSchets({ ...schets, punten: [...schets.punten, p] });
    else if (schets.soort !== 'pin') setSchets({ ...schets, tot: p });
  }

  function op() {
    // A click without a drag leaves nothing behind.
    if (schets && !isLeeg(schets)) onVormen([...vormen, schets]);
    setSchets(null);
  }

  function maakOngedaan() {
    const laatste = vormen[vormen.length - 1];
    onVormen(ongedaan(vormen));
    if (laatste?.soort === 'pin') onPin(null);
  }

  return (
    <div className="mt-3">
      <p id="meld-teken-uitleg" className="text-[12.5px] text-text-muted">{t('melden.teken.uitleg')}</p>
      <div role="toolbar" aria-label={t('melden.teken.gereedschap')} className="mt-2 flex flex-wrap gap-1.5">
        {GEREEDSCHAPPEN.map((g) => (
          <button
            key={g}
            type="button"
            aria-pressed={gereedschap === g}
            data-testid={`meld-gereedschap-${g}`}
            onClick={() => setGereedschap(g)}
            className={KNOP}
          >
            {t(`melden.teken.${g}`)}
          </button>
        ))}
        <button type="button" data-testid="meld-ongedaan" disabled={vormen.length === 0} onClick={maakOngedaan} className={KNOP}>
          {t('melden.teken.ongedaan')}
        </button>
      </div>
      <canvas
        ref={ref}
        width={basis.width}
        height={basis.height}
        aria-describedby="meld-teken-uitleg"
        data-testid="meld-teken-canvas"
        onPointerDown={neer}
        onPointerMove={beweeg}
        onPointerUp={op}
        onPointerCancel={() => setSchets(null)}
        onLostPointerCapture={() => setSchets(null)}
        className="mt-2 block h-auto w-full touch-none rounded-sm border border-border"
      />
    </div>
  );
}
